import type { ReactElement } from 'react';

import { useStackModal } from '@rango-dev/internal-profile/hooks';
import {
  Box,
  Button,
  Card,
  Flex,
  IconButton,
  Separator,
  Skeleton,
  Text,
  Tooltip,
} from '@rango-dev/whale';
import { Check, Copy, Pencil, Share2, Star, TrendingUp, WalletMinimal } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { CONNECT_WALLETS_MODAL_ID, PROFILE_WALLETS_MODAL_ID } from '../../../constants/modals';
import { useAppDispatch, useAppSelector } from '../../../hooks/reduxHooks';
import {
  dispatchCancellableAction,
  fetchProfileSummary,
  showPrivateProfileSelector,
} from '../../../state/ProfileSlice';
import { profileSummarySelector } from '../../../state/ProfileSlice/selectors';
import { shouldExcludeWalletInProfileWalletsList } from '../../../utils/wallets/general';
import ConnectWalletModal from '../../ConnectWalletModal';
import { SimpleNotification } from '../../SimpleNotification';
import { Avatar } from '../Avatar';
import { shortenUsername } from '../Leaderboard/Leaderboard.helpers';
import { LogOut } from '../LogOut';
import { ProfileWalletsModal } from '../ProfileWalletsModal';
import { ShareProgressModal } from '../ShareProgress/ShareProgressModal';
import { UnexpectedError } from '../UnexpectedError';
import { UpdateUsernameModal } from '../UpdateUsernameModal';
import { UserScoreProgress } from '../UserScoreProgress';

import styles from './ProfileCard.module.css';
import { ProfileCardItem } from './ProfileCardItem';

const RESET_INTERVAL = 2_000;
const USER_AVATAR_SIZE = 48;
const USERNAME_START_CHARS = 10;
const USERNAME_END_CHARS = 5;
const ACTION_ICON_SIZE = 18;
const COPY_ICON_SIZE = 16;
const SHARE_ICON_SIZE = 18;
const WALLET_ICON_SIZE = 18;

export function ProfileCard(): ReactElement {
  const params = useParams<{ username: string }>();
  const {
    data: profileSummary,
    loading,
    error,
  } = useAppSelector((state) => profileSummarySelector(state, params.username));
  const showPrivateProfile = useAppSelector((state) =>
    showPrivateProfileSelector(state, params.username),
  );
  const { t } = useTranslation();
  const [isCopied, setIsCopied] = useState(false);
  const copyResetTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const [showShareProgressModal, setShowShareProgressModal] = useState(false);
  const [showUpdateUsernameModal, setShowUpdateUsernameModal] = useState(false);
  const [showUsernameUpdateNotification, setShowUsernameUpdateNotification] = useState(false);

  const { push, clear } = useStackModal();
  const shouldOpenConnectWalletsModalRef = useRef(false);
  const shouldOpenProfileWalletsModalRef = useRef(false);
  const dispatch = useAppDispatch();

  const displayedUsername = profileSummary?.username
    ? shortenUsername({
        username: profileSummary.username,
        startChars: USERNAME_START_CHARS,
        endChars: USERNAME_END_CHARS,
      })
    : undefined;

  const userItems = {
    score: {
      title: t('Score'),
      description: t('Your Score will be updated every hour.'),
      icon: (
        <Flex
          justify="center"
          align="center"
          width={{ initial: '32px', sm: '48px', lg: '40px' }}
          height={{ initial: '32px', sm: '48px', lg: '40px' }}
          className={styles.scoreIconCircle}>
          <Star width="50%" height="auto" color="var(--orange-8)" />
        </Flex>
      ),
    },
    rank: {
      title: t('Rank'),
      description: t('Your Rank will be updated every hour.'),
      icon: (
        <Flex
          justify="center"
          align="center"
          width={{ initial: '32px', sm: '48px', lg: '40px' }}
          height={{ initial: '32px', sm: '48px', lg: '40px' }}
          className={styles.rankIconCircle}>
          <TrendingUp width="50%" height="auto" color="var(--tomato-9)" />
        </Flex>
      ),
    },
  } as const;

  // Clear the pending "Copied" reset if the card unmounts first.
  useEffect(
    () => () => {
      if (copyResetTimeoutRef.current) {
        clearTimeout(copyResetTimeoutRef.current);
      }
    },
    [],
  );

  const handleCopyUsername = () => {
    const username = profileSummary?.username ?? '';
    if (!username || !navigator.clipboard) {
      return;
    }
    navigator.clipboard
      .writeText(username)
      .then(() => {
        setIsCopied(true);
        if (copyResetTimeoutRef.current) {
          clearTimeout(copyResetTimeoutRef.current);
        }
        copyResetTimeoutRef.current = setTimeout(() => setIsCopied(false), RESET_INTERVAL);
      })
      .catch(() => {
        // Clipboard unavailable or rejected — don't show the "Copied" state.
      });
  };

  const refetchProfileSummary = () => {
    if (params.username) {
      dispatchCancellableAction(dispatch, fetchProfileSummary, {
        username: params.username,
        public: false,
      });
    }
  };

  const toggleUpdateUsernameModal = () => setShowUpdateUsernameModal((prevState) => !prevState);

  const toggleUpdateUsernameNotification = () => {
    setShowUsernameUpdateNotification((prevState) => !prevState);
  };

  const setShouldOpenProfileWalletsModal = (shouldOpenProfileWalletsModal?: boolean) => {
    shouldOpenProfileWalletsModalRef.current = !!shouldOpenProfileWalletsModal;
  };

  const setShouldOpenConnectWalletsModal = (shouldOpenConnectWalletsModal?: boolean) => {
    shouldOpenConnectWalletsModalRef.current = !!shouldOpenConnectWalletsModal;
  };

  const handleOpenProfileWalletsModal = () => {
    push({
      id: PROFILE_WALLETS_MODAL_ID,
      renderContent: (index) => (
        <ProfileWalletsModal
          indexInStack={index}
          setShouldOpenConnectWalletsModal={setShouldOpenConnectWalletsModal}
        />
      ),
      onExit: () => {
        if (shouldOpenConnectWalletsModalRef.current) {
          shouldOpenConnectWalletsModalRef.current = false;
          handleOpenConnectWalletsModal();
        }
      },
    });
  };

  const handleOpenConnectWalletsModal = () => {
    push({
      id: CONNECT_WALLETS_MODAL_ID,
      renderContent: (index) => (
        <ConnectWalletModal
          indexInStack={index}
          shouldExcludeWallet={shouldExcludeWalletInProfileWalletsList}
          title={t('Choose wallet for your profile')}
          afterConnect={() => {
            setShouldOpenProfileWalletsModal(true);
            clear();
          }}
          onDetachedConfirm={() => {
            setShouldOpenProfileWalletsModal(true);
            clear();
          }}
          detachedConfirmText={t('Continue')}
        />
      ),
      onExit: () => {
        if (shouldOpenProfileWalletsModalRef.current) {
          shouldOpenProfileWalletsModalRef.current = false;
          handleOpenProfileWalletsModal();
        }
      },
    });
  };

  return (
    <Card variant="surface" size={{ initial: '2', sm: '3' }}>
      {error ? (
        <UnexpectedError context="profile-page-profile-card" onRetry={refetchProfileSummary} />
      ) : (
        <Flex direction="column" gap="5">
          <Flex justify="between" align="center" gap="3">
            <Flex align="center" gap={{ initial: '2', sm: '4' }}>
              {loading ? (
                <Skeleton
                  width={`${USER_AVATAR_SIZE}px`}
                  height={`${USER_AVATAR_SIZE}px`}
                  className={styles.avatarSkeleton}
                />
              ) : (
                <Avatar
                  size={{ initial: '3', sm: '4' }}
                  username={profileSummary?.username || ''}
                />
              )}
              {loading ? (
                <Skeleton width="128px" height="20px" />
              ) : (
                <Text size={{ initial: '1', sm: '2' }} weight="regular">
                  {displayedUsername}
                </Text>
              )}
            </Flex>
            <Flex gap="4" align="center">
              {loading ? (
                <>
                  <Skeleton width="32px" height="32px" className={styles.actionButtonSkeleton} />
                  {showPrivateProfile && (
                    <Skeleton width="32px" height="32px" className={styles.actionButtonSkeleton} />
                  )}
                </>
              ) : (
                <>
                  {showPrivateProfile && (
                    <Tooltip content={t('Edit your username')} side="bottom" sideOffset={14}>
                      <IconButton
                        id="profile-card-edit-username"
                        variant="outline"
                        color="cyan"
                        size="2"
                        radius="full"
                        onClick={toggleUpdateUsernameModal}
                        aria-label={t('Edit your username')}>
                        <Pencil size={ACTION_ICON_SIZE} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {!showPrivateProfile && (
                    <Tooltip
                      content={isCopied ? t('Copied to clipboard') : t('Copy Username')}
                      side="bottom"
                      sideOffset={14}
                      open={isCopied || undefined}>
                      <IconButton
                        id="profile-card-copy-username"
                        variant="outline"
                        color="cyan"
                        size="2"
                        radius="full"
                        onClick={handleCopyUsername}
                        aria-label={t('Copy Username')}>
                        {isCopied ? (
                          <Check size={COPY_ICON_SIZE} />
                        ) : (
                          <Copy size={COPY_ICON_SIZE} />
                        )}
                      </IconButton>
                    </Tooltip>
                  )}
                  {showPrivateProfile && <LogOut />}
                </>
              )}
            </Flex>
          </Flex>

          <Flex gap="4">
            {Object.keys(userItems).map((key) => {
              const userItemKey = key as keyof typeof userItems;
              const { title, description, icon } = userItems[userItemKey];
              return (
                <ProfileCardItem
                  key={userItemKey}
                  title={title}
                  description={description}
                  icon={icon}
                  {...(loading
                    ? { loading: true }
                    : { loading: false, value: profileSummary?.[userItemKey] ?? 0 })}
                />
              );
            })}
          </Flex>

          {showPrivateProfile && <UserScoreProgress />}

          {showPrivateProfile && (
            <>
              <Separator size="4" />
              <Flex gap={{ initial: '2', xs: '4' }} direction={{ initial: 'column', xs: 'row' }}>
                <Box flexGrow="1">
                  <Button
                    id="profile-card-share-profile"
                    variant="outline"
                    color="cyan"
                    size="3"
                    radius="full"
                    disabled={loading}
                    className={styles.fullWidthButton}
                    onClick={() => setShowShareProgressModal(true)}>
                    <Share2 size={SHARE_ICON_SIZE} />
                    {t('Share profile')}
                  </Button>
                </Box>
                <Box flexGrow="1">
                  <Button
                    id="profile-card-profile-wallet"
                    variant="solid"
                    color="cyan"
                    size="3"
                    radius="full"
                    disabled={loading}
                    className={styles.fullWidthButton}
                    onClick={handleOpenProfileWalletsModal}>
                    <WalletMinimal size={WALLET_ICON_SIZE} />
                    {t('Profile wallet')}
                  </Button>
                </Box>
              </Flex>
              <ShareProgressModal
                onClose={() => setShowShareProgressModal(false)}
                open={showShareProgressModal}
              />
            </>
          )}
          <SimpleNotification
            message={t('Your username has been successfully updated!')}
            open={showUsernameUpdateNotification}
            setOpen={toggleUpdateUsernameNotification}
            severity="success"
          />
          {profileSummary?.username && (
            <UpdateUsernameModal
              open={showUpdateUsernameModal}
              onClose={toggleUpdateUsernameModal}
              onUpdate={toggleUpdateUsernameNotification}
            />
          )}
        </Flex>
      )}
    </Card>
  );
}
